import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../utils/i18n';
import ReactCountryFlag from 'react-country-flag';

interface Language {
  code: string;
  name: string;
  countryCode: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', countryCode: 'US' },
  { code: 'fr', name: 'French', countryCode: 'FR' },
  { code: 'ar', name: 'Arabic', countryCode: 'MA' }
];

interface LanguageSelectorProps {
  currentLanguage?: string;
  onLanguageChange?: (language: string) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage = 'en',
  onLanguageChange
}) => {
  const { i18n, t } = useTranslation('languageSelector');
  const isRTL = ['ar', 'he', 'fa', 'ur'].includes(currentLanguage || i18n.language);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    languages.find(lang => lang.code === (currentLanguage || i18n.language)) || languages[0]
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync with i18n language changes
  useEffect(() => {
    const lang = languages.find(l => l.code === (currentLanguage || i18n.language));
    if (lang) {
      setSelectedLanguage(lang);
    }
  }, [currentLanguage, i18n.language]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = (language: Language) => {
    setSelectedLanguage(language);
    setIsOpen(false);
    onLanguageChange?.(language.code);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Language Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-4 py-2.5 text-dark-blue hover:text-vibrant-orange transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-vibrant-orange focus:ring-offset-2 focus:ring-offset-white rounded-md hover:bg-gray-100"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Current Language Flag */}
        <ReactCountryFlag
          countryCode={selectedLanguage.countryCode}
          svg
          style={{
            width: '1.5em',
            height: '1.5em',
          }}
          title={t(`languages.${selectedLanguage.code}`)}
          className="flex-shrink-0"
        />

        {/* Current Language */}
        <span className="font-semibold text-base">{t(`languages.${selectedLanguage.code}`)}</span>

        {/* Dropdown Arrow */}
        <svg
          className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 w-52 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden`}>
          {/* Dropdown Arrow Pointer */}
          <div className={`absolute -top-2 w-4 h-4 bg-white border-l border-t border-gray-200 transform rotate-45 ${isRTL ? 'left-6' : 'right-6'}`}></div>

          <div className="py-1">
            {languages.map((language) => {
              return (
                <button
                  key={language.code}
                  onClick={() => handleLanguageSelect(language)}
                  className={`w-full flex items-center gap-3 px-4 py-3 ${isRTL ? 'text-right' : 'text-left'} transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg ${selectedLanguage.code === language.code
                      ? 'bg-vibrant-orange bg-opacity-10 text-vibrant-orange hover:bg-gray-50'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {/* Flag Icon */}
                  <ReactCountryFlag
                    countryCode={language.countryCode}
                    svg
                    style={{
                      width: '1.5em',
                      height: '1.5em',
                    }}
                    title={t(`languages.${language.code}`)}
                    className="flex-shrink-0"
                  />

                  {/* Language Name */}
                  <span className="font-medium text-sm flex-grow">{t(`languages.${language.code}`)}</span>

                  {/* Selected Indicator */}
                  {selectedLanguage.code === language.code && (
                    <svg className="w-4 h-4 text-vibrant-orange flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
